import type {AddItemOptionsProps} from '@Components/poster-editor/components/poster-add-item-bar/poster-add-item-bar.types';
import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import AiImagePanel from '@Panels/ai-image-panel';
import {getNoOfGridItems} from '@Components/base-grid';
import {onAddItems} from '@Components/poster-editor/library/poster-editor-open-modals';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {AddItemButton} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-button';
import {getActivityIndicatorTypeForAiImage} from '@Components/poster-editor/poster-editor-reducer';
import {ActivityIndicatorType} from '@Components/activity-indicator/activity-indicator.types';
import {useAppSelector} from '@/hooks';
import styles from './add-ai-images-button.module.scss';

const GRID_ID = 'TEXT_TO_IMAGE_LEFT_POP_OVER';
const TOP_OFFSET = 50;
const OFFSET_PADDING_BIAS = 18.2;

export function AddAiImagesButton({...props}: AddItemOptionsProps): ReactElement<any> {
  const [isOpened, setIsOpened] = useState(false);
  const numberOfGridItems = useAppSelector((state) => {
    return getNoOfGridItems(state, GRID_ID);
  });
  const activityIndicatorForAiImage = useAppSelector(getActivityIndicatorTypeForAiImage);

  const getSelector = (): ReactElement<any> => {
    return (
      <AddItemButton
        isDisabled={false}
        isSelected={isOpened}
        id="toolbar-ai-images"
        text={window.i18next.t('pmwjs_ai_images')}
        icon="icon-ai-image"
        className={isOpened ? styles.selectedButton : undefined}
        activityIndicatorType={!isOpened ? activityIndicatorForAiImage : undefined}
        textClasses={getClassForText()}
        iconClasses={styles.aiIcon}
      />
    );
  };

  const getClassForText = (): string => {
    const commonClasses = '-text-align-center';
    switch (activityIndicatorForAiImage) {
      case ActivityIndicatorType.LOADING:
        return `${commonClasses} ${styles.aiImageGeneratingText}`;
      case ActivityIndicatorType.SUCCESS:
        return `${commonClasses} ${styles.iconReady}`;
      default:
        return `${commonClasses} ${styles.aiImageText}`;
    }
  };

  const getPopupContent = (): ReactElement<any> => {
    return (
      <AiImagePanel
        onAddSelected={onAddItems}
        onClose={() => {
          // setIsOpened(false);
        }}
        shouldLoadDataUrlImage
        gridID={GRID_ID}
        className="_full-height"
      />
    );
  };

  return (
    <Dropdown
      open={isOpened}
      doCloseOnInnerClick={false}
      className="js-toolbar-text"
      id="ai-image-pop-over"
      key="toolbar-ai-images-dropdown"
      alignment={ALIGNMENT_TYPE.RIGHT}
      selector={getSelector()}
      popup={getPopupContent()}
      popUpHasCustomHeight
      maxHeight={606}
      topOffset={TOP_OFFSET}
      arrowOffset={TOP_OFFSET - OFFSET_PADDING_BIAS}
      popUpHasCustomWidth
      useDefaultArrow
      popupClassName={` ${styles.gridContent} ${numberOfGridItems > 0 ? styles.fullGridContent : styles.emptyGridContent} ${props.popupClassName}`}
      popupSpacingClass="spacing-p-0"
      arrowClasses={props.arrowClasses}
      onOpen={(): void => {
        if (!isOpened) {
          trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_MENU_OPEN);
          setIsOpened(true);
        }
      }}
      onClose={(): void => {
        if (isOpened) {
          setIsOpened(false);
        }
      }}
    />
  );
}
